import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList } from 'lucide-react';
import Icon, { type IconName } from '../../../components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Pagination from '@/components/Pagination';
import { PageLayout } from '@/components/ui/PageLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { LoadingState } from '../../../components/AsyncStates';
import { cn } from '@/utils/cn';
import { useMyAssignments } from '@/hooks/useMyAssignments';
import { useMyWorkPoints } from '@/hooks/useMyWorkPoints';
import { acceptAssignment, declineAssignment } from '@/api/TournamentManagement/refereeApi';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import { MatchStatus, MatchRole, AssignmentResponse, resolveEnum } from '@/models/tournament';
import { useCurrency } from '@/hooks/useCurrency';
import type { MyAssignmentItem } from '@/models/tournament';
import DeclineAssignmentModal from './components/DeclineAssignmentModal';
import MyScheduleTab from './components/MyScheduleTab';
import WorkPointsTab from './components/WorkPointsTab';
import { formatLocalDateParam, parseApiUtc, parseApiWallClock } from '@/utils/apiDate';
import { appDate, appDateTime, appTime, appWeekday } from '@/utils/appDateFormat';

type HubTab = 'assignments' | 'schedule' | 'workpoints';

/* The two tab strips on this page were plain <button> lists: no role="tablist"/"tab"/"tabpanel",
   no aria-selected, no aria-controls and no arrow-key movement, with the active tab marked by a
   border colour alone. Both are now the repo's Radix-backed ui/Tabs (which also carries the CO-356
   RTL direction fix), restyled from the default segmented pill back to the underline strip this
   page already had, so the markup reads the same and the semantics come for free. */
const TAB_LIST_CLASS =
  'flex h-auto w-full justify-start rounded-none bg-transparent p-0 border-b border-border overflow-x-auto overflow-y-hidden';
const TAB_TRIGGER_CLASS =
  'gap-2 whitespace-nowrap rounded-none border-b-2 border-transparent -mb-px px-5 py-3 text-muted-foreground ' +
  'hover:text-foreground hover:border-border ' +
  'data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-primary ' +
  'data-[state=active]:text-primary data-[state=active]:font-semibold';

const MyAssignments: React.FC = () => {
  const { t } = useTranslation('tournament');
  const { format: formatCurrency } = useCurrency();
  const [hubTab, setHubTab] = useState<HubTab>('assignments');
  const [assignmentTab, setAssignmentTab] = useState<'upcoming' | 'past'>('upcoming');

  // Get current month date range. NEVER toISOString() — east of UTC that yields the PREVIOUS day,
  // so the last day of the month (tonight's kickoff) would fall outside the inclusive server bound.
  const now = new Date();
  const startOfMonth = formatLocalDateParam(new Date(now.getFullYear(), now.getMonth(), 1));
  const endOfMonth = formatLocalDateParam(new Date(now.getFullYear(), now.getMonth() + 1, 0));

  // Both hooks already return `error`; this page used to destructure everything EXCEPT that, and had
  // no error branch at all — so a failed fetch fell straight through to "No upcoming assignments"
  // and told a referee they had no work when the request had actually failed.
  const {
    data: workPointsSummary,
    error: workPointsError,
    refetch: refetchWorkPoints,
  } = useMyWorkPoints({ startDate: startOfMonth, endDate: endOfMonth });
  const {
    data: assignments,
    loading: assignmentsLoading,
    error: assignmentsError,
    refetch,
  } = useMyAssignments({ pageSize: 100 });

  // `loading` goes true on EVERY refetch (accept/decline both call it). Gating the list on it tore
  // the whole tab out and dropped a spinner in its place. Spinner only when there is nothing to
  // show yet; later fetches keep the content mounted and dim it.
  const isInitialLoad = assignmentsLoading && !assignments;
  const isRefetching = assignmentsLoading && !!assignments;

  const { toast, showToast, dismissToast } = useToast();
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineMatchId, setDeclineMatchId] = useState<number>(0);
  const [declineMatchDescription, setDeclineMatchDescription] = useState('');
  const [respondingMatchId, setRespondingMatchId] = useState<number | null>(null);

  const statusKeys: Record<MatchStatus, string> = {
    [MatchStatus.Scheduled]: 'Scheduled',
    [MatchStatus.InProgress]: 'In Progress',
    [MatchStatus.Completed]: 'Completed',
    [MatchStatus.Closed]: 'Closed',
    [MatchStatus.UnderRevision]: 'Under Revision',
    [MatchStatus.Cancelled]: 'Cancelled',
  };

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const getStatusColor = (status: MatchStatus): string => {
    switch (status) {
      case MatchStatus.Scheduled:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
      case MatchStatus.InProgress:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
      case MatchStatus.Completed:
        return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
      case MatchStatus.Closed:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      case MatchStatus.UnderRevision:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
      case MatchStatus.Cancelled:
        return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  // Kickoff is company wall-clock — rendered as stored, never shifted by the viewer's timezone.
  const formatDate = (dateString: string): string => {
    const date = parseApiWallClock(dateString) ?? new Date(dateString);
    return `${appWeekday(date, 'short')} ${appDate(date)}`;
  };

  const formatTime = (dateString: string): string => {
    const date = parseApiWallClock(dateString) ?? new Date(dateString);
    return appTime(date);
  };

  // The response deadline IS an instant (the server compares it to UTC now) — viewer-local time.
  const formatDeadline = (dateString: string): string => {
    const date = parseApiUtc(dateString) ?? new Date(dateString);
    return appDateTime(date);
  };

  const handleAccept = async (matchId: number) => {
    setRespondingMatchId(matchId);
    try {
      const result = await acceptAssignment({ matchId });
      if (result.errors.length) {
        showToast(result.errors[0], 'error');
      } else {
        showToast(t('Assignment accepted successfully'), 'success');
        refetch();
      }
    } catch {
      showToast(t('Failed to accept assignment'), 'error');
    } finally {
      setRespondingMatchId(null);
    }
  };

  const handleOpenDeclineModal = (assignment: MyAssignmentItem) => {
    setDeclineMatchId(assignment.matchId);
    setDeclineMatchDescription(`${assignment.team1Name} vs ${assignment.team2Name}`);
    setShowDeclineModal(true);
  };

  const handleDecline = async (matchId: number, reason?: string) => {
    const result = await declineAssignment({ matchId, reason });
    if (result.errors.length) {
      showToast(result.errors[0], 'error');
      throw new Error(result.errors[0]);
    }
    showToast(t('Assignment declined'), 'success');
    refetch();
  };

  // Filters for upcoming + past lists
  const [searchFilter, setSearchFilter] = useState('');
  const [tournamentFilter, setTournamentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [responseFilter, setResponseFilter] = useState('');

  // Pagination state — independent for upcoming and past
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const applyFilters = (list: MyAssignmentItem[]) => {
    let out = list;
    if (searchFilter.trim()) {
      const q = searchFilter.trim().toLowerCase();
      out = out.filter((a) =>
        (a.team1Name ?? '').toLowerCase().includes(q) ||
        (a.team2Name ?? '').toLowerCase().includes(q) ||
        (a.location ?? '').toLowerCase().includes(q) ||
        (a.tournamentName ?? '').toLowerCase().includes(q)
      );
    }
    if (tournamentFilter) out = out.filter((a) => String(a.tournamentId ?? '') === tournamentFilter);
    if (statusFilter !== '') {
      const want = parseInt(statusFilter);
      out = out.filter((a) => {
        const s = (resolveEnum(MatchStatus, a.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled;
        return s === want;
      });
    }
    if (roleFilter !== '') {
      const want = parseInt(roleFilter);
      out = out.filter((a) => {
        const r = (resolveEnum(MatchRole, a.role) as MatchRole) ?? MatchRole.HeadReferee;
        return r === want;
      });
    }
    if (responseFilter !== '') {
      const want = parseInt(responseFilter);
      out = out.filter((a) => {
        const r = (resolveEnum(AssignmentResponse, a.responseStatus) as AssignmentResponse) ?? AssignmentResponse.Pending;
        return r === want;
      });
    }
    return out;
  };

  const upcomingAssignments = useMemo(() => {
    if (!assignments?.items) return [] as MyAssignmentItem[];
    const base = assignments.items.filter((a) => {
      const s = (resolveEnum(MatchStatus, a.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled;
      return s === MatchStatus.Scheduled || s === MatchStatus.InProgress || s === MatchStatus.UnderRevision;
    });
    return applyFilters(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, searchFilter, tournamentFilter, statusFilter, roleFilter, responseFilter]);

  const pastAssignments = useMemo(() => {
    if (!assignments?.items) return [] as MyAssignmentItem[];
    const base = assignments.items.filter((a) => {
      const s = (resolveEnum(MatchStatus, a.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled;
      return s === MatchStatus.Completed || s === MatchStatus.Closed || s === MatchStatus.Cancelled;
    });
    return applyFilters(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, searchFilter, tournamentFilter, statusFilter, roleFilter, responseFilter]);

  // Reset paging when filters or tab change
  useEffect(() => { setUpcomingPage(1); setPastPage(1); }, [searchFilter, tournamentFilter, statusFilter, roleFilter, responseFilter]);

  // Unique tournaments from my assignments for the tournament filter dropdown
  const myTournamentOptions = useMemo(() => {
    const items = assignments?.items ?? [];
    const seen = new Map<string, string>();
    items.forEach((a) => {
      const key = String(a.tournamentId ?? '');
      if (key && !seen.has(key)) seen.set(key, a.tournamentName ?? key);
    });
    return [{ value: '', label: t('All Tournaments') }, ...Array.from(seen.entries()).map(([value, label]) => ({ value, label }))];
  }, [assignments, t]);

  const paginate = <T,>(list: T[], page: number): { slice: T[]; totalPages: number } => {
    const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
    const start = (page - 1) * pageSize;
    return { slice: list.slice(start, start + pageSize), totalPages };
  };
  const upcomingPaged = paginate(upcomingAssignments, upcomingPage);
  const pastPaged = paginate(pastAssignments, pastPage);

  const clearFilters = () => {
    setSearchFilter('');
    setTournamentFilter('');
    setStatusFilter('');
    setRoleFilter('');
    setResponseFilter('');
  };
  const hasActiveFilters = !!(searchFilter || tournamentFilter || statusFilter || roleFilter || responseFilter);

  const renderAssignmentCard = (assignment: MyAssignmentItem, showPoints: boolean) => (
    <div
      key={assignment.matchId}
      className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
      data-testid={`assignment-card-${assignment.matchId}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="text-xs text-muted-foreground mb-1">{assignment.tournamentName}</p>
          <p className="text-sm font-semibold text-foreground">
            {assignment.team1Name} <span className="text-muted-foreground/70">{t('vs')}</span> {assignment.team2Name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {showPoints && assignment.workPoints > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" data-testid={`work-points-badge-${assignment.matchId}`}>
              <Icon name="Zap" size={12} className="me-1" />
              {assignment.workPoints} {t('pts')}
            </span>
          )}
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor((resolveEnum(MatchStatus, assignment.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled)}`}>
            {t(statusKeys[(resolveEnum(MatchStatus, assignment.matchStatus) as MatchStatus) ?? MatchStatus.Scheduled] ?? 'Unknown')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-muted-foreground">
        <div className="flex items-center">
          <Icon name="Calendar" size={12} className="me-1.5 text-muted-foreground/70" />
          <span>{formatDate(assignment.scheduledDateTime)}</span>
        </div>
        <div className="flex items-center">
          <Icon name="Clock" size={12} className="me-1.5 text-muted-foreground/70" />
          <span>{formatTime(assignment.scheduledDateTime)}</span>
        </div>
        <div className="flex items-center">
          <Icon name="MapPin" size={12} className="me-1.5 text-muted-foreground/70" />
          <span className="truncate">{assignment.location}</span>
        </div>
      </div>

      <div className="mt-2 flex items-center text-xs">
        <Icon name="UserCog" size={12} className="me-1.5 text-primary" />
        <span className="font-medium text-primary">{t(roleKeys[(resolveEnum(MatchRole, assignment.role) as MatchRole) ?? MatchRole.HeadReferee] ?? 'Unknown')}</span>
      </div>

      {/* Accept/Decline buttons or status badge.
          Accept used to be a DEFAULT-variant Button carrying `bg-green-600 text-white` overrides and
          Decline an outline one carrying red ones. cn() is twMerge, so those class fights resolved
          inconsistently against the variant's own fill. Both are plain variants now: Accept is the
          semantic `success` variant, Decline just opens the confirm dialog (the irreversible step
          lives there and IS destructive). */}
      {resolveEnum(AssignmentResponse, assignment.responseStatus) === AssignmentResponse.Pending && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="success"
            onClick={() => handleAccept(assignment.matchId)}
            loading={respondingMatchId === assignment.matchId}
            disabled={respondingMatchId !== null}
            iconName={respondingMatchId === assignment.matchId ? null : 'CheckCircle'}
            iconSize={14}
            data-testid={`accept-btn-${assignment.matchId}`}
          >
            {t('Accept')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleOpenDeclineModal(assignment)}
            disabled={respondingMatchId !== null}
            iconName="XCircle"
            iconSize={14}
            data-testid={`decline-btn-${assignment.matchId}`}
          >
            {t('Decline')}
          </Button>
          {assignment.responseDeadline && (
            <span className="text-xs text-muted-foreground ms-auto">
              <Icon name="Clock" size={12} className="inline me-1" />
              {t('Respond by')} {formatDeadline(assignment.responseDeadline)}
            </span>
          )}
        </div>
      )}
      {resolveEnum(AssignmentResponse, assignment.responseStatus) === AssignmentResponse.Accepted && (
        <div className="mt-3">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
            data-testid={`accepted-badge-${assignment.matchId}`}
          >
            <Icon name="CheckCircle" size={14} className="me-1" />
            {t('Accepted')}
          </span>
        </div>
      )}
      {resolveEnum(AssignmentResponse, assignment.responseStatus) === AssignmentResponse.Declined && (
        <div className="mt-3">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
            data-testid={`declined-badge-${assignment.matchId}`}
          >
            <Icon name="XCircle" size={14} className="me-1" />
            {t('Declined')}
          </span>
        </div>
      )}
    </div>
  );

  const hubTabs: { id: HubTab; label: string; icon: string }[] = [
    { id: 'assignments', label: t('Assignments'), icon: 'ClipboardList' },
    { id: 'schedule', label: t('My Schedule'), icon: 'CalendarDays' },
    { id: 'workpoints', label: t('Work Points'), icon: 'Zap' },
  ];

  // These three numbers used to be three big-number cards under the header (and were repeated
  // verbatim on the Work Points tab). They belong in the PageLayout header, which is where every
  // other page in the module puts its counts. On a work-points fetch failure they show "—" rather
  // than a confident 0.
  const monthlyStats = [
    {
      label: `${t('Total Work Points')} · ${t('This Month')}`,
      value: workPointsError ? '—' : (workPointsSummary?.totalWorkPoints ?? 0),
    },
    {
      label: `${t('Currency Equivalent')} · ${t('This Month')}`,
      value: workPointsError ? '—' : formatCurrency(workPointsSummary?.totalCurrencyAmount ?? 0),
    },
    {
      label: t('Matches This Month'),
      value: workPointsError ? '—' : (workPointsSummary?.matchCount ?? 0),
    },
  ];

  const dimWhileRefetching = cn(
    'transition-opacity duration-200 ease-out motion-reduce:transition-none',
    isRefetching && 'opacity-60 pointer-events-none',
  );

  return (
    <div data-testid="my-assignments-page">
      <PageLayout
        title={t('My Assignments')}
        description={t('View your referee assignments, schedule, and work points')}
        icon={ClipboardList}
        accentColor="amber"
        maxWidth="5xl"
        stats={monthlyStats}
        statsColumns={3}
      >
        {/* Error states — a failed fetch is now said out loud instead of rendering as an empty list. */}
        {assignmentsError && (
          <div
            role="alert"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3"
            data-testid="my-assignments-error"
          >
            <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-red-900 dark:text-red-200">{t('Error loading assignments')}</p>
              <p className="text-sm text-red-700 dark:text-red-300">{assignmentsError}</p>
            </div>
            <Button variant="outline" size="sm" onClick={refetch} className="ms-auto" data-testid="my-assignments-retry-btn">
              {t('Try Again')}
            </Button>
          </div>
        )}
        {workPointsError && (
          <div
            role="alert"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3"
            data-testid="my-assignments-workpoints-error"
          >
            <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-red-900 dark:text-red-200">{t('Error loading work points')}</p>
              <p className="text-sm text-red-700 dark:text-red-300">{workPointsError}</p>
            </div>
            <Button variant="outline" size="sm" onClick={refetchWorkPoints} className="ms-auto" data-testid="my-assignments-workpoints-retry-btn">
              {t('Try Again')}
            </Button>
          </div>
        )}

        <Tabs value={hubTab} onValueChange={(v) => setHubTab(v as HubTab)}>
          <TabsList className={TAB_LIST_CLASS} data-testid="hub-tabs">
            {hubTabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className={TAB_TRIGGER_CLASS}
                data-testid={`hub-tab-${tab.id}`}
              >
                <Icon name={tab.icon as IconName} size={16} className="flex-shrink-0" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="assignments" className="mt-6" data-testid="hub-content-assignments">
            {isInitialLoad ? (
              <div className="p-6">
                <LoadingState title={t('Loading data...')} />
              </div>
            ) : (
              <Tabs value={assignmentTab} onValueChange={(v) => setAssignmentTab(v as 'upcoming' | 'past')}>
                <TabsList className={TAB_LIST_CLASS} data-testid="assignment-tabs">
                  <TabsTrigger value="upcoming" className={TAB_TRIGGER_CLASS} data-testid="tab-upcoming">
                    <Icon name="Calendar" size={16} className="flex-shrink-0" />
                    {t('Upcoming Assignments')} ({upcomingAssignments.length})
                  </TabsTrigger>
                  <TabsTrigger value="past" className={TAB_TRIGGER_CLASS} data-testid="tab-past">
                    <Icon name="CheckCircle" size={16} className="flex-shrink-0" />
                    {t('Past Assignments')} ({pastAssignments.length})
                  </TabsTrigger>
                </TabsList>

                {/* Filters — shared by both sub-tabs, so they sit outside the panels. */}
                <div className="bg-card rounded-lg border border-border p-4 mt-4 mb-4" data-testid="my-assignments-filters">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div className="lg:col-span-2">
                      <Input
                        type="text"
                        placeholder={t('Search teams, location, tournament...')}
                        value={searchFilter}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchFilter(e.target.value)}
                        data-testid="my-assignments-search"
                      />
                    </div>
                    <Select
                      value={tournamentFilter}
                      onChange={(v: string) => setTournamentFilter(v)}
                      placeholder={t('All Tournaments')}
                      options={myTournamentOptions}
                      data-testid="my-assignments-tournament-filter"
                    />
                    <Select
                      value={statusFilter}
                      onChange={(v: string) => setStatusFilter(v)}
                      placeholder={t('All Statuses')}
                      options={[
                        { value: '', label: t('All Statuses') },
                        { value: '0', label: t('Scheduled') },
                        { value: '1', label: t('In Progress') },
                        { value: '2', label: t('Completed') },
                        { value: '3', label: t('Closed') },
                        { value: '4', label: t('Under Revision') },
                        { value: '5', label: t('Cancelled') },
                      ]}
                      data-testid="my-assignments-status-filter"
                    />
                    <Select
                      value={roleFilter}
                      onChange={(v: string) => setRoleFilter(v)}
                      placeholder={t('All Roles')}
                      options={[
                        { value: '', label: t('All Roles') },
                        { value: String(MatchRole.HeadReferee), label: t('Head Referee') },
                        { value: String(MatchRole.AssistantReferee), label: t('Assistant Referee') },
                        { value: String(MatchRole.FourthOfficial), label: t('Fourth Official') },
                        { value: String(MatchRole.Commissioner), label: t('Commissioner') },
                      ]}
                      data-testid="my-assignments-role-filter"
                    />
                    <Select
                      value={responseFilter}
                      onChange={(v: string) => setResponseFilter(v)}
                      placeholder={t('All Responses')}
                      options={[
                        { value: '', label: t('All Responses') },
                        { value: String(AssignmentResponse.Pending), label: t('Pending') },
                        { value: String(AssignmentResponse.Accepted), label: t('Accepted') },
                        { value: String(AssignmentResponse.Declined), label: t('Declined') },
                      ]}
                      data-testid="my-assignments-response-filter"
                    />
                  </div>
                  {hasActiveFilters && (
                    <div className="mt-3 flex items-center justify-end">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={clearFilters}
                        iconName="X"
                        iconSize={14}
                        data-testid="my-assignments-clear-filters"
                      >
                        {t('Clear Filters')}
                      </Button>
                    </div>
                  )}
                </div>

                <TabsContent value="upcoming" className="mt-0">
                  <div
                    className={cn('space-y-3', dimWhileRefetching)}
                    aria-busy={isRefetching}
                    data-testid="assignment-list-upcoming"
                  >
                    {upcomingAssignments.length > 0 ? (
                      <>
                        {upcomingPaged.slice.map((a) => renderAssignmentCard(a, false))}
                        {upcomingAssignments.length > pageSize && (
                          <div className="pt-2">
                            <Pagination
                              currentPage={upcomingPage}
                              totalPages={upcomingPaged.totalPages}
                              onPageChange={setUpcomingPage}
                              pageSize={pageSize}
                              onPageSizeChange={(s) => { setPageSize(s); setUpcomingPage(1); setPastPage(1); }}
                              totalCount={upcomingAssignments.length}
                              variant="full"
                            />
                          </div>
                        )}
                      </>
                    ) : !assignmentsError && (
                      <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="empty-upcoming">
                        <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground/40 mb-4" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">{t('No upcoming assignments')}</h3>
                        <p className="text-muted-foreground">{t('You have no upcoming match assignments.')}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="past" className="mt-0">
                  <div
                    className={cn('space-y-3', dimWhileRefetching)}
                    aria-busy={isRefetching}
                    data-testid="assignment-list-past"
                  >
                    {pastAssignments.length > 0 ? (
                      <>
                        {pastPaged.slice.map((a) => renderAssignmentCard(a, true))}
                        {pastAssignments.length > pageSize && (
                          <div className="pt-2">
                            <Pagination
                              currentPage={pastPage}
                              totalPages={pastPaged.totalPages}
                              onPageChange={setPastPage}
                              pageSize={pageSize}
                              onPageSizeChange={(s) => { setPageSize(s); setUpcomingPage(1); setPastPage(1); }}
                              totalCount={pastAssignments.length}
                              variant="full"
                            />
                          </div>
                        )}
                      </>
                    ) : !assignmentsError && (
                      <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="empty-past">
                        <Icon name="CheckCircle" size={48} className="mx-auto text-muted-foreground/40 mb-4" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">{t('No past assignments')}</h3>
                        <p className="text-muted-foreground">{t('You have no completed match assignments.')}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </TabsContent>

          <TabsContent value="schedule" className="mt-6" data-testid="hub-content-schedule">
            <MyScheduleTab />
          </TabsContent>

          <TabsContent value="workpoints" className="mt-6" data-testid="hub-content-workpoints">
            <WorkPointsTab />
          </TabsContent>
        </Tabs>

        <DeclineAssignmentModal
          isOpen={showDeclineModal}
          onClose={() => setShowDeclineModal(false)}
          onDecline={handleDecline}
          matchId={declineMatchId}
          matchDescription={declineMatchDescription}
        />
        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </PageLayout>
    </div>
  );
};

export default MyAssignments;
